import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Star,
  Monitor,
  Gamepad2,
  Palette,
  Code2,
  ShieldCheck,
} from "lucide-react";
import styles from "../styles";
import {
  logowithclouds,
  UFAlogo,
  ESAlogo,
  CFEFlogo,
  activision,
  disney,
  hasbro,
  lego,
  riotgames,
  teacherskyler,
  teacheraustin,
  teacherevan,
  teachertyler,
  stepupforstudents,
} from "../../assets/images";
import HeroForm from "../../components/ScholarshipFunnel/HeroForm";
import ReviewsCompact from "../../components/Home/layout/ReviewsCompact";
import { FAQ } from "../../components";

const HalfStar = ({ size = "w-4 h-4" }) => (
  <div className={`relative ${size}`}>
    <Star className={`${size} text-orangeprimary absolute top-0 left-0`} />
    <div className="overflow-hidden w-[60%] absolute top-0 left-0">
      <Star className={`${size} fill-orangeprimary text-orangeprimary`} />
    </div>
  </div>
);

const WistiaVideo = () => {
  useEffect(() => {
    const script1 = document.createElement("script");
    script1.src = "https://fast.wistia.com/player.js";
    script1.async = true;
    document.body.appendChild(script1);

    const script2 = document.createElement("script");
    script2.src = "https://fast.wistia.com/embed/yjjd2r7jub.js";
    script2.async = true;
    script2.type = "module";
    document.body.appendChild(script2);

    return () => {
      document.body.removeChild(script1);
      document.body.removeChild(script2);
    };
  }, []);

  return (
    <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
      <wistia-player
        media-id="yjjd2r7jub"
        aspect="1.7777777777777777"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

const ScholarshipAd = () => {
  return (
    <div className="bg-white">
      <Helmet>
        <title>Free Trial Class | Tech Tails</title>
        <meta
          name="description"
          content="Give your student a free coding class with Tech Tails. Live one-on-one instruction in game design, coding, and digital art. We accept Utah Fits All, ESA Arizona, Step Up for Students, and other education scholarships."
        />
      </Helmet>

      {/* ── HERO ── */}
      <section className="relative bg-gradient-to-br from-darkprimary via-[#04466d] to-darkprimary overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-4 sm:pb-8">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-14">
            <div className="flex-1 text-center lg:text-left">
              <img src={logowithclouds} alt="Tech Tails" className="h-10 sm:h-12 mb-4 mx-auto lg:mx-0" />

              <h1 className="font-nunito text-white text-3xl sm:text-5xl font-extrabold leading-[1.15] mb-3">
                Their New{" "}
                <span className="relative inline-block">
                  Favorite
                  <span className="absolute -bottom-0.5 left-0 w-full h-2 bg-pinkprimary -skew-x-[12deg] rounded-sm -z-10" />
                </span>{" "}
                Class
              </h1>

              <p className="font-poppins text-white/80 text-base sm:text-lg leading-relaxed mb-4 max-w-md mx-auto lg:mx-0">
                Live, one-on-one STEM classes in coding, game design, and digital
                art taught by industry pros for students of all abilities.
              </p>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 sm:gap-3 mb-4">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5">
                  <div className="flex items-center">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-orangeprimary text-orangeprimary" />
                    ))}
                    <HalfStar size="w-3.5 h-3.5" />
                  </div>
                  <span className="text-white/90 text-xs sm:text-sm font-medium">4.6 from parents</span>
                </div>
                <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-orangeprimary" />
                  <span className="text-white/90 text-xs sm:text-sm font-medium">Scholarships accepted</span>
                </div>
              </div>

              <div className="flex items-center gap-2 justify-center lg:justify-start flex-wrap">
                {[
                  { src: UFAlogo, alt: "Utah Fits All" },
                  { src: ESAlogo, alt: "ESA Arizona" },
                  { src: CFEFlogo, alt: "CFEF" },
                  { src: stepupforstudents, alt: "Step Up for Students" },
                ].map((logo) => (
                  <div key={logo.alt} className="bg-white rounded-md px-2.5 py-1">
                    <img src={logo.src} alt={logo.alt} className="h-5 sm:h-6" />
                  </div>
                ))}
                <span className="text-white/40 text-xs font-poppins">& more</span>
              </div>
            </div>

            <div className="w-full lg:w-[420px] flex-shrink-0">
              <HeroForm />
            </div>
          </div>

          {/* Company logos */}
          <div className="mt-6 pt-5 border-t border-white/10">
            <p className="font-poppins text-white/40 text-xs uppercase tracking-widest text-center mb-3">
              Our instructors have professional experience with
            </p>
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-12">
              <img src={disney} alt="Disney" className="h-6 sm:h-8" />
              <img src={activision} alt="Activision" className="h-6 sm:h-8" />
              <img src={riotgames} alt="Riot Games" className="h-6 sm:h-8" />
              <img src={hasbro} alt="Hasbro" className="h-5 sm:h-7" />
              <img src={lego} alt="Lego" className="h-5 sm:h-7" />
            </div>
          </div>
        </div>
      </section>

      {/* ── VIDEO ── */}
      <section className="py-6 sm:py-10">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className={`${styles.biggerheadingdark} text-center mb-2`}>
            See What a{" "}
            <span className="relative inline-block">
              Class
              <span className="absolute -bottom-0.5 left-0 w-full h-2 bg-pinkprimary -skew-x-[12deg] rounded-sm -z-10" />
            </span>
            {" "}Looks Like
          </h2>
          <p className="font-poppins text-darkprimary/60 text-center mb-5 max-w-xl mx-auto text-base">
            Every class is live, one-on-one, and tailored to your student
          </p>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <WistiaVideo />
          </div>
        </div>
      </section>

      {/* ── MEET THE TEAM ── */}
      <section className="py-6 sm:py-10 bg-darkprimary">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className={`${styles.biggerheadingwhite} mb-2`}>
            Meet the{" "}
            <span className="relative inline-block">
              Instructors
              <span className="absolute -bottom-0.5 left-0 w-full h-2 bg-pinkprimary -skew-x-[12deg] rounded-sm -z-10" />
            </span>
          </h2>
          <p className="font-poppins text-white/60 mb-5 max-w-lg mx-auto text-base">
            Industry professionals who adapt to every student's learning style
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-5 max-w-4xl mx-auto">
            {[
              { img: teacherskyler, name: "Skyler", tags: ["Full Stack Dev", "10+ Yrs Exp"] },
              { img: teacheraustin, name: "Austin", tags: ["Unity Dev", "AI Engineer"] },
              { img: teacherevan, name: "Evan", tags: ["Disney & WB Vet", "3D Art"] },
              { img: teachertyler, name: "Tyler", tags: ["Game Dev Bachelor's", "Unity"] },
              { img: logowithclouds, name: "Jacob", tags: ["Lua & Scratch", "MC Creator"], isLogo: true },
            ].map((t) => (
              <div key={t.name} className="text-center group w-[105px] sm:w-[120px]">
                {t.isLogo ? (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-darkprimary border-[3px] border-white/20 mx-auto mb-1.5 flex items-center justify-center p-2 group-hover:border-pinkprimary transition-colors">
                    <img src={t.img} alt={t.name} className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-[3px] border-white/20 mx-auto mb-1.5 group-hover:border-pinkprimary transition-colors">
                    <img src={t.img} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="font-nunito font-bold text-white text-sm leading-tight mb-1">
                  {t.name}
                </p>
                <div className="flex flex-wrap justify-center gap-1">
                  {t.tags.map((tag) => (
                    <span key={tag} className="font-poppins text-[9px] sm:text-[10px] bg-white/10 text-white/70 rounded-full px-1.5 py-0.5">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-6 sm:py-10 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className={`${styles.biggerheadingdark} text-center mb-2`}>
            How It{" "}
            <span className="relative inline-block">
              Works
              <span className="absolute -bottom-0.5 left-0 w-full h-2 bg-pinkprimary -skew-x-[12deg] rounded-sm -z-10" />
            </span>
          </h2>
          <p className="font-poppins text-darkprimary/60 text-center mb-5 max-w-xl mx-auto text-base">
            Getting started takes less than 2 minutes
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                step: "1",
                title: "Sign Up",
                desc: "Fill out the quick form and we'll reach out to schedule your student's free class.",
                accent: "bg-pinkprimary",
              },
              {
                step: "2",
                title: "Try a Free Class",
                desc: "Your student gets a full 45-minute live session tailored to their interests and learning style.",
                accent: "bg-orangeprimary",
              },
              {
                step: "3",
                title: "Keep Learning",
                desc: "Love it? Pick a plan that fits. We accept scholarships and offer flexible scheduling.",
                accent: "bg-darkprimary",
              },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className={`${item.accent} text-white w-8 h-8 rounded-full flex items-center justify-center font-nunito font-bold text-sm flex-shrink-0`}>
                    {item.step}
                  </div>
                  <h3 className="font-nunito text-darkprimary text-lg font-bold">
                    {item.title}
                  </h3>
                </div>
                <p className="font-poppins text-darkprimary/60 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT THEY LEARN ── */}
      <section className="py-6 sm:py-10 bg-darkprimary">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className={`${styles.biggerheadingwhite} text-center mb-2`}>
            What Your Student Will{" "}
            <span className="relative inline-block">
              Learn
              <span className="absolute -bottom-0.5 left-0 w-full h-2 bg-orangeprimary -skew-x-[12deg] rounded-sm -z-10" />
            </span>
          </h2>
          <p className="font-poppins text-white/60 text-center mb-5 max-w-xl mx-auto text-base">
            Real STEM skills through creative, hands-on projects they love
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              {
                icon: <Gamepad2 className="w-6 h-6" />,
                title: "Game Design",
                desc: "Build real games from scratch using industry tools",
              },
              {
                icon: <Code2 className="w-6 h-6" />,
                title: "Coding",
                desc: "Learn programming through hands-on projects",
              },
              {
                icon: <Palette className="w-6 h-6" />,
                title: "Digital Art",
                desc: "Create 3D models, characters, and animations",
              },
              {
                icon: <Monitor className="w-6 h-6" />,
                title: "Roblox & Minecraft",
                desc: "Mod, script, and build in games they love",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white/5 rounded-xl p-4 sm:p-5 border border-white/10 text-center"
              >
                <div className="w-10 h-10 bg-pinkprimary/20 rounded-lg flex items-center justify-center mx-auto mb-2 text-pinkprimary">
                  {item.icon}
                </div>
                <h3 className="font-nunito text-white text-sm sm:text-base font-bold mb-1">
                  {item.title}
                </h3>
                <p className="font-poppins text-white/50 text-xs sm:text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <ReviewsCompact sectionClassName="py-6 sm:py-10" />

      {/* ── FINAL CTA ── */}
      <section className="relative py-6 sm:py-10 bg-gradient-to-br from-darkprimary via-[#04466d] to-darkprimary overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />

        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <h2 className={`${styles.biggerheadingwhite} mb-2`}>
            Ready to Get{" "}
            <span className="relative inline-block">
              Started?
              <span className="absolute -bottom-0.5 left-0 w-full h-2 bg-pinkprimary -skew-x-[12deg] rounded-sm -z-10" />
            </span>
          </h2>
          <p className="font-poppins text-white/60 text-center mb-5 max-w-xl mx-auto text-base">
            Claim your student's free trial class. No commitment, no payment,
            just a great first experience.
          </p>
          <div className="max-w-[420px] mx-auto">
            <HeroForm />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <FAQ bgClassName="bg-white" />
    </div>
  );
};

export default ScholarshipAd;
